"use client";

import React from "react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Plus, Funnel as Filter } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
function ShopProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-12 lg:gap-16">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="space-y-6">
            <div className="aspect-[4/5] rounded-lg bg-muted/40 animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-2/3 rounded bg-muted/40 animate-pulse" />
              <div className="h-3 w-1/3 rounded bg-muted/40 animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground font-default">
        Failed to load research compounds.{" "}
        <Button variant="link" size="sm" onClick={refetch} className="h-auto p-0 underline">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-muted-foreground font-default">
        No compounds available for research at this time.
      </p>;
  }
  return <>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 lg:gap-16">
        {products.map((product, i) => <article data-index={i} key={product.id} className="group flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-[600ms] ease-out group-hover:opacity-90 group-hover:scale-[1.02]" />}
                <span aria-hidden="true" className="absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-lg bg-background/90 text-foreground opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <Plus className="h-4 w-4" strokeWidth={1.25} />
                </span>
              </div>
            </Link>

            <div className="mt-6">
              <Link to={product.link}>
                <h3 className="font-default text-[18px] font-semibold leading-tight text-white">
                  {product.title}
                </h3>
              </Link>

              {product.categories && product.categories.length > 0 && <p className="mt-2 text-[12px] uppercase tracking-[0.2em] text-muted-foreground font-default">
                  {product.categories[0].name}
                </p>}

              <p className="mt-3 text-[13px] text-muted-foreground line-clamp-2">
                {(product as any).description}
              </p>

              {product.price !== undefined && <p className="mt-2 text-[14px] text-white font-default">
                  ${product.price}
                </p>}

              <div className="mt-5">
                <AddToCartButton productId={product.id} variant="link" className="w-full" buttonClassName="bg-primary text-black font-bold uppercase tracking-widest rounded-lg" text="Add to Cart" showQuantity={false} />
              </div>
            </div>
          </article>)}
      </div>

      {totalPages > 1 && <div className="mt-32 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-4">
              <PaginationItem>
                <Button variant="ghost" size="sm" onClick={prevPage} disabled={!hasPrev} className="text-muted-foreground hover:text-white hover:bg-transparent disabled:opacity-30">
                  <PaginationPrevious className="pointer-events-none p-0" />
                </Button>
              </PaginationItem>

              {Array.from({
            length: totalPages
          }).map((_, idx) => {
            const pageNum = idx + 1;
            const isActive = pageNum === currentPage;
            return <PaginationItem key={pageNum} data-index={idx}>
                    <Button variant="ghost" size="sm" onClick={() => setPage(pageNum)} className={`min-w-[2rem] px-2 font-default text-[14px] hover:bg-transparent ${isActive ? "text-white underline underline-offset-[6px] decoration-1" : "text-muted-foreground hover:text-white"}`}>
                      {pageNum}
                    </Button>
                  </PaginationItem>;
          })}

              <PaginationItem>
                <Button variant="ghost" size="sm" onClick={nextPage} disabled={!hasNext} className="text-muted-foreground hover:text-white hover:bg-transparent disabled:opacity-30">
                  <PaginationNext className="pointer-events-none p-0" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>}
    </>;
}
export default function ShopContent() {
  const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(current_wp_query_params);
  return <section data-section-id="2388" id="shop-content" className="bg-background text-foreground pt-8 pb-24 lg:pt-12 lg:pb-48">
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-12">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <aside className="hidden lg:block lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <ProductsFilter />
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pb-8 border-b border-border/60">
                <div className="flex items-center justify-between w-full sm:w-auto gap-4">
                  <div className="text-[13px] text-muted-foreground font-default tracking-wide">
                    Elite Research Compounds
                  </div>
                  
                  <div className="lg:hidden">
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Filter className="size-4" />
                          Filters
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="left" className="w-[300px] sm:w-[400px] bg-background border-r border-border/60">
                        <SheetHeader className="pb-6">
                          <SheetTitle className="text-white font-default uppercase tracking-widest text-sm">Filters</SheetTitle>
                        </SheetHeader>
                        <div className="mt-4">
                          <ProductsFilter />
                        </div>
                      </SheetContent>
                    </Sheet>
                  </div>
                </div>

                <ProductSortDropdown label="Sort" className="w-full sm:w-[180px]" />
              </div>

              <ShopProductsConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}